import React, { useState, useMemo } from 'react';
import { styled } from '@xstyled/styled-components';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  RenderProps,
  registry
} from '~/training/sequence/templates/items/Item';
import { CARD } from '~/training/sequence/templates/items/itemIds';
import { useEditorStore } from '~/shell/Store';
import { useReorderCards } from './useReorderCards';
import DuplicateIcon from '~/assets/icons/copy.svg';
import TrashIcon from '~/assets/icons/trashIcon.svg';
import EditIcon from '~/assets/icons/editIconBig.svg';
import { CardModal } from './CardModal';
import { determineCardTypeFromTemplates, findCardInSequence } from './utils';
import { CardTemplate } from './templates/types';
import { extractCardDataFromTemplate } from './cardUtils';
import { fieldRegistry } from './edit-modal/fieldRegistry';
import './edit-modal/registerFields';

const StyledListItem = styled.li`
  position: relative;
  width: fit-content;

  cursor: grab;

  img {
    -webkit-user-drag: none;
    user-drag: none;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    flex-shrink: 0;
  }
`;

interface Props {
  cardId: string | number;
  width?: Record<string, string> | string;
  height?: Record<string, string> | string;
  cardTemplates: CardTemplate[];
}

export type EditableCardWrapperDefinition = ItemDefinition<
  'EditableCardWrapper',
  Props
>;

export class EditableCardWrapper extends Item<'EditableCardWrapper', Props> {
  protected validate() {
    if (this.definition.items?.length !== 1) {
      throw new ItemValidationError(
        'EditableCardWrapper must have exactly 1 child'
      );
    }

    if (!this.definition.items?.some(i => i.type === CARD)) {
      throw new ItemValidationError(
        'EditableCardWrapper must have a CARD child'
      );
    }
  }

  protected render({ children, cardId, cardTemplates }: RenderProps<Props>) {
    const { contentEditable, removeSubItem, addSubItem, sequence } =
      useEditorStore();

    const {
      onDragStart,
      onDrop,
      onDragOver,
      onDragEnter,
      onDragEnd,
      onPointerDown
    } = useReorderCards();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const cardData = useMemo(
      () => (sequence ? findCardInSequence(sequence, cardId) : undefined),
      [sequence?.items, cardId]
    );

    const supportedCardTypes = cardTemplates.map(t => t.type);

    const cardType = useMemo(() => {
      if (!cardData) return supportedCardTypes[0];
      const determinedType = determineCardTypeFromTemplates(
        cardData,
        cardTemplates
      );
      if (supportedCardTypes.includes(determinedType)) {
        return determinedType;
      }
      return supportedCardTypes[0];
    }, [cardData, cardTemplates]);

    if (!cardData) {
      return <>{children}</>;
    }

    const handleDuplicate = () => {
      if (!sequence || !sequence.items) return;

      const sourceCard: any = sequence.items.find(
        (it: any) => String(it.id) === String(cardId)
      );

      if (!sourceCard) return;

      const template = cardTemplates.find(t => t.type === cardType);
      if (!template) return;

      const currentPosition =
        typeof sourceCard.position === 'number' ? sourceCard.position : 0;
      const position = currentPosition + 1;

      const fieldData = extractCardDataFromTemplate(sourceCard, template);

      const apiData: Record<string, any> = {
        type: 'CARD',
        position,
        parent_id: null
      };

      template.fields.forEach(field => {
        const currentValue = fieldData[field.name];

        if (field.type === 'input' || field.type === 'textarea') {
          apiData[field.name] = currentValue;
        } else {
          const apiSerializer = fieldRegistry.getApiSerializer(field.type);
          if (apiSerializer) {
            apiSerializer(field, currentValue, apiData);
          }
        }
      });

      const newItem: Record<string, any> = {
        id: `temp-${Date.now()}`,
        __typename: 'Card',
        position
      };

      template.fields.forEach(field => {
        const currentValue = fieldData[field.name];
        const storeSerializer = fieldRegistry.getStoreSerializer(field.type);
        if (storeSerializer) {
          storeSerializer(field, currentValue, newItem);
        }
      });

      addSubItem({
        sequenceId: sequence.id,
        data: apiData,
        newItem
      });
    };

    const handleDelete = () => {
      removeSubItem(cardId);
    };

    if (!contentEditable) {
      return <StyledListItem>{children}</StyledListItem>;
    }

    return (
      <StyledListItem
        draggable
        onPointerDown={e => {
          onPointerDown(String(cardId), e);
        }}
        data-card-id={cardId}
        onDragStart={e => onDragStart(String(cardId), e)}
        onDrop={() => onDrop()}
        onDragOver={onDragOver}
        onDragEnter={() => onDragEnter(String(cardId))}
        onDragEnd={onDragEnd}
      >
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 6,
            zIndex: 10,
            background: 'transparent',
            borderRadius: 0,
            padding: 0
          }}
        >
          <ActionButton
            type="button"
            aria-label="Edit card"
            onClick={e => {
              e.stopPropagation();
              setIsEditModalOpen(true);
            }}
            title="Edit card"
          >
            <EditIcon />
          </ActionButton>
          <ActionButton
            type="button"
            aria-label="Duplicate card"
            onClick={e => {
              e.stopPropagation();
              handleDuplicate();
            }}
            title="Duplicate card"
          >
            <DuplicateIcon />
          </ActionButton>
          <ActionButton
            type="button"
            aria-label="Delete card"
            onClick={e => {
              e.stopPropagation();
              handleDelete();
            }}
            title="Delete card"
          >
            <TrashIcon />
          </ActionButton>
        </div>

        {children}

        {isEditModalOpen &&
          (() => {
            const template = cardTemplates.find(t => t.type === cardType);

            if (!template) {
              console.error(
                `[EditableCardWrapper] No template found for card type: ${cardType}`
              );
              return null;
            }

            return (
              <CardModal
                mode="edit"
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                cardId={cardId}
                fields={template.fields}
                validation={template.validation}
                modalTitle={template.editModalTitle}
              />
            );
          })()}
      </StyledListItem>
    );
  }
}

registry['EditableCardWrapper'] = EditableCardWrapper;
